import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import { Button } from './ui-kit';
import { getTokenValue } from '../api/tokens';
import { getPermissionChipSx } from '../utils/serviceColors';

function TokenViewDialog({ open, onClose, token, fullToken, canViewValue = false }) {
  const [copied, setCopied] = useState(false);
  const [tokenValue, setTokenValue] = useState(fullToken);
  const [loading, setLoading] = useState(false);

  // Получаем полный токен из API если он не передан и есть права
  useEffect(() => {
    if (open && token && !fullToken && canViewValue) {
      setLoading(true);
      getTokenValue(token.id)
        .then(response => {
          if (response.success && response.data.tokenValue) {
            setTokenValue(response.data.tokenValue);
          }
        })
        .catch(error => {
          console.error('Error fetching token value:', error);
          toast.error('Failed to load token value');
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (fullToken) {
      setTokenValue(fullToken);
    } else if (!canViewValue) {
      setTokenValue(null);
    }
  }, [open, token, fullToken, canViewValue]);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
    if (text === tokenValue) {
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const handleCopyFullToken = () => {
    if (tokenValue) {
      handleCopy(tokenValue);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  if (!token) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Token Details
        {token.isActive ? (
          <Chip
            icon={<CheckCircleIcon />}
            label="Active"
            color="success"
            size="small"
            sx={{ ml: 2 }}
          />
        ) : (
          <Chip
            icon={<BlockIcon />}
            label="Revoked"
            color="error"
            size="small"
            sx={{ ml: 2 }}
          />
        )}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Warning for new token */}
          {tokenValue && (
            <Alert severity="warning">
              <AlertTitle>Save this token now!</AlertTitle>
              This is the only time you will see the full token value. Make sure to save it securely.
              <TextField
                fullWidth
                value={tokenValue}
                sx={{ mt: 2, fontFamily: 'monospace' }}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => handleCopy(tokenValue)}
                        edge="end"
                        color={copied ? 'success' : 'primary'}
                      >
                        {copied ? <CheckCircleIcon /> : <ContentCopyIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Alert>
          )}

          {/* Loading indicator */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Token Information */}
          <List>
            <ListItem>
              <ListItemText
                primary="Token Name"
                secondary={token.tokenName}
              />
            </ListItem>
            <Divider component="li" />

            <ListItem>
              <ListItemText
                primary="Token Prefix"
                secondary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" fontFamily="monospace">
                      {token.tokenPrefix}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleCopy(token.tokenPrefix)}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                }
              />
            </ListItem>
            <Divider component="li" />

            {token.ownerUserId && (
              <>
                <ListItem>
                  <ListItemText
                    primary="Owner"
                    secondary={
                      token.ownerUserId.email || token.ownerUserId.fullName || 'Unknown'
                    }
                  />
                </ListItem>
                <Divider component="li" />
              </>
            )}

            <ListItem>
              <ListItemText
                primary="Permissions"
                secondary={
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {token.permissions && token.permissions.length > 0 ? (
                      [...token.permissions]
                        .sort((a, b) => {
                          const sa = a.service || '';
                          const sb = b.service || '';
                          if (sa !== sb) return sa.localeCompare(sb);
                          return (a.resource || '').localeCompare(b.resource || '');
                        })
                        .map((perm) => (
                          <Chip
                            key={perm._id}
                            label={perm.name || `${perm.service}.${perm.resource}.${perm.action}`}
                            size="small"
                            sx={getPermissionChipSx(perm)}
                          />
                        ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No permissions
                      </Typography>
                    )}
                  </Box>
                }
              />
            </ListItem>
            <Divider component="li" />

            <ListItem>
              <ListItemText
                primary="Expires"
                secondary={
                  <Typography
                    variant="body2"
                    color={isExpired(token.expiresAt) ? 'error' : 'text.secondary'}
                  >
                    {formatDate(token.expiresAt)}
                    {isExpired(token.expiresAt) && ' (Expired)'}
                  </Typography>
                }
              />
            </ListItem>
            <Divider component="li" />

            {token.lastUsedAt && (
              <>
                <ListItem>
                  <ListItemText
                    primary="Last Used"
                    secondary={formatDate(token.lastUsedAt)}
                  />
                </ListItem>
                <Divider component="li" />
              </>
            )}

            <ListItem>
              <ListItemText
                primary="Usage Count"
                secondary={token.usageCount || 0}
              />
            </ListItem>
            <Divider component="li" />

            <ListItem>
              <ListItemText
                primary="Created"
                secondary={formatDate(token.createdAt)}
              />
            </ListItem>

            {token.description && (
              <>
                <Divider component="li" />
                <ListItem>
                  <ListItemText
                    primary="Description"
                    secondary={token.description}
                  />
                </ListItem>
              </>
            )}
          </List>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
        {tokenValue && (
          <Button onClick={handleCopyFullToken} variant="contained" color="primary">
            Copy Token
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default TokenViewDialog;
